import React, { useMemo } from "react";

import { useAppSelector } from "../../../app/hooks";

import { CreateChargeAssetLinkageFields } from "../../../api/charges";

import {
  selectAssetsGroupedByClass,
} from "../Logic/assetsSlice";

import SelectableEntityCard from "./SelectableEntityCard";
import { FolderOpen } from "lucide-react";
import { ASSET_CLASS_LABELS } from "../../../api/assets";


const formatEnumLabel = (value: string) =>
  value
    .split("_")
    .map(
      word =>
        word.charAt(0) +
        word.slice(1).toLowerCase(),
    )
    .join(" ");

interface AssetSelectionStepProps {
  excludeAssetIds?: string[];

  selectedAssetIds: CreateChargeAssetLinkageFields[];
  
  onSelectionChange: (
    selectedAssetIds: CreateChargeAssetLinkageFields[],
  ) => void;
}

const AssetSelectionStep: React.FC<
  AssetSelectionStepProps
> = ({
  excludeAssetIds = [],
  selectedAssetIds,
  onSelectionChange,
}) => {
  const groupedAssets = useAppSelector(selectAssetsGroupedByClass);

  const filteredGroupedAssets = useMemo(() => {
    const grouped: typeof groupedAssets = {};

    Object.entries(groupedAssets).forEach(
      ([assetClass, assets]) => {
        const filteredAssets = assets.filter(
          (asset) =>
            !excludeAssetIds.includes(
              asset.asset_id,
            ),
        );

        if (filteredAssets.length > 0) {
          grouped[assetClass] =
            filteredAssets;
        }
      },
    );

    return grouped;
  }, [groupedAssets,excludeAssetIds]);

  const handleAssetToggle = (
    assetId: string,
  ) => {
    const isSelected = selectedAssetIds.some((asset) =>asset.asset_id === assetId);

    const updatedSelection = isSelected
      ? selectedAssetIds.filter(
          (asset) =>
            asset.asset_id !== assetId,
        )
      : [
          ...selectedAssetIds,
          {
            asset_id: assetId,
          },
        ];

    onSelectionChange(updatedSelection);
  };

  const hasAssets = Object.keys(filteredGroupedAssets).length > 0;

  return (
     !hasAssets ? (
      <div className="flex min-h-[280px] flex-col items-center justify-center gap-3">
        <FolderOpen
          size={40}
          className="text-[#A3A3A3]"
        />

        <div className="text-sm font-medium text-[#0A0A0A]">
          No assets available
        </div>

        <div className="text-xs text-[#737373]">
          There are no assets available to select.
        </div>
      </div>
    ): (
    <div className="flex flex-col gap-4">
      {Object.entries(filteredGroupedAssets).map(
        ([assetClass, assets]) => (
          <div
            key={assetClass}
            className="flex flex-col"
          >
            <p
              className="
                mb-2
                text-[12px]
                font-medium
                leading-4
                text-[#737373]
              "
            >
              {
                ASSET_CLASS_LABELS[
                  assetClass as keyof typeof ASSET_CLASS_LABELS
                ]
              }
            </p>

            <div className="flex flex-col gap-3">
              {assets.map(asset => (
                <SelectableEntityCard
                  key={asset.asset_id}
                  title={ asset.asset_name ??"Unnamed Asset"}
                  subtitle={`${asset.ext_asset_id} • ${formatEnumLabel(asset.asset_type)}`}
                  selected={selectedAssetIds.some(selectedAsset =>selectedAsset.asset_id ===asset.asset_id)}
                  onClick={() =>
                    handleAssetToggle(asset.asset_id)
                  }
                />
              ))}
            </div>
          </div>
        ),
      )}
    </div>
    ) 
  );
};

export default AssetSelectionStep;